import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface ObjectContextMenuProps {
  x: number;
  y: number;
  selectedNodeCount?: number;
  isInstance?: boolean;
  isMask?: boolean;
  onLocate: () => void;
  onGoToParentComponent: () => void;
  onDetachInstance: () => void;
  onGroup?: () => void;
  onWrapInFrame?: () => void;
  onToggleMask: () => void;
  onCopyStyle: () => void;
  onCopyFill: () => void;
  onCopyStroke: () => void;
  onCopyEffects: () => void;
  onPasteStyle: () => void;
  onCopySvg: () => void;
  onCopyPng: (scale: number) => void;
  onFlatten: () => void;
  onPlanarize: () => void;
  onOutlineStroke: () => void;
  onCreateComponent: () => void;
  convertTarget?: "frame" | "group";
  onConvertFrameGroup: () => void;
  isFrame?: boolean;
  hasScript?: boolean;
  allowScripts?: boolean;
  onAttachOrChangeScript?: () => void;
  onEditScript?: () => void;
  onDetachScript?: () => void;
  onRunScript?: () => void;
  onExportSvg: () => void;
  onExportPng: (scale: number) => void;
  onClose: () => void;
}

export function ObjectContextMenu({
  x,
  y,
  selectedNodeCount = 1,
  isInstance = false,
  isMask = false,
  onLocate,
  onGoToParentComponent,
  onDetachInstance,
  onGroup,
  onWrapInFrame,
  onToggleMask,
  onCopyStyle,
  onCopyFill,
  onCopyStroke,
  onCopyEffects,
  onPasteStyle,
  onCopySvg,
  onCopyPng,
  onFlatten,
  onPlanarize,
  onOutlineStroke,
  onCreateComponent,
  convertTarget,
  onConvertFrameGroup,
  isFrame = false,
  hasScript = false,
  allowScripts = false,
  onAttachOrChangeScript,
  onEditScript,
  onDetachScript,
  onRunScript,
  onExportSvg,
  onExportPng,
  onClose,
}: ObjectContextMenuProps) {
  const isMulti = selectedNodeCount > 1;
  const menuRef = useRef<HTMLDivElement | null>(null);
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(() => ({ left: x, top: y }));
  const [activeSubmenu, setActiveSubmenu] = useState<"style" | "copyAs" | "exportAs" | undefined>(undefined);
  const [submenuAnchor, setSubmenuAnchor] = useState<DOMRect | undefined>(undefined);
  const [submenuPosition, setSubmenuPosition] = useState(() => ({ left: x, top: y }));

  useLayoutEffect(() => {
    function updatePosition(): void {
      const menu = menuRef.current;
      const margin = 8;
      const width = menu?.offsetWidth ?? 240;
      const height = menu?.offsetHeight ?? 320;
      setPosition({
        left: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - width - margin)),
        top: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - height - margin)),
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [isInstance, x, y]);

  useLayoutEffect(() => {
    if (!activeSubmenu || !submenuAnchor) {
      return;
    }
    const anchor = submenuAnchor;

    function updateSubmenuPosition(): void {
      const width = submenuRef.current?.offsetWidth ?? 160;
      const height = submenuRef.current?.offsetHeight ?? 192;
      setSubmenuPosition(clampedSubmenuPosition(anchor, width, height));
    }

    updateSubmenuPosition();
    window.addEventListener("resize", updateSubmenuPosition);
    return () => window.removeEventListener("resize", updateSubmenuPosition);
  }, [activeSubmenu, submenuAnchor]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  function openSubmenu(submenu: NonNullable<typeof activeSubmenu>, element: HTMLElement): void {
    const anchor = element.getBoundingClientRect();
    setActiveSubmenu(submenu);
    setSubmenuAnchor(anchor);
    setSubmenuPosition(clampedSubmenuPosition(anchor, Math.min(208, window.innerWidth - 16), submenu === "style" ? 176 : 128));
  }

  function closeSubmenu(): void {
    setActiveSubmenu(undefined);
    setSubmenuAnchor(undefined);
  }

  const submenu = activeSubmenu ? (
    <div
      ref={submenuRef}
      className="fixed z-50 overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 text-[12px] text-slate-200 shadow-xl shadow-black/30"
      style={{ left: submenuPosition.left, top: submenuPosition.top, width: "min(13rem, calc(100vw - 16px))", maxHeight: "calc(100vh - 16px)" }}
      role="menu"
      onMouseEnter={() => setActiveSubmenu(activeSubmenu)}
      onMouseLeave={closeSubmenu}
    >
      {activeSubmenu === "style" ? (
        <>
          <button type="button" className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]" onClick={onCopyStyle} role="menuitem">
            <span>Copy style</span>
            <span className="pl-4 text-[11px] text-slate-500">Opt+Cmd/Ctrl+C</span>
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onCopyFill} role="menuitem">
            Copy fill
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onCopyStroke} role="menuitem">
            Copy stroke
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onCopyEffects} role="menuitem">
            Copy effects
          </button>
          <div className="my-1 h-px bg-white/10" />
          <button type="button" className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]" onClick={onPasteStyle} role="menuitem">
            <span>Paste style</span>
            <span className="pl-4 text-[11px] text-slate-500">Opt+Cmd/Ctrl+V</span>
          </button>
        </>
      ) : null}
      {activeSubmenu === "copyAs" ? (
        <>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onCopySvg} role="menuitem">
            SVG
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={() => onCopyPng(1)} role="menuitem">
            PNG
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={() => onCopyPng(2)} role="menuitem">
            PNG @2x
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={() => onCopyPng(3)} role="menuitem">
            PNG @3x
          </button>
        </>
      ) : null}
      {activeSubmenu === "exportAs" ? (
        <>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onExportSvg} role="menuitem">
            SVG
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={() => onExportPng(1)} role="menuitem">
            PNG
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={() => onExportPng(2)} role="menuitem">
            PNG @2x
          </button>
          <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={() => onExportPng(3)} role="menuitem">
            PNG @3x
          </button>
        </>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        aria-label="Close context menu"
        className="fixed inset-0 z-40 cursor-default"
        onClick={onClose}
        onContextMenu={event => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="fixed z-50 overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 text-[12px] text-slate-200 shadow-xl shadow-black/30"
        style={{ left: position.left, top: position.top, width: "min(15rem, calc(100vw - 16px))", maxHeight: "calc(100vh - 16px)" }}
        role="menu"
      >
        {!isMulti ? (
          <button
            type="button"
            className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
            onClick={onLocate}
            onMouseEnter={closeSubmenu}
            onFocus={closeSubmenu}
            role="menuitem"
          >
            Locate
          </button>
        ) : null}
        {!isMulti && isInstance ? (
          <>
            <button
              type="button"
              className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
              onClick={onGoToParentComponent}
              onMouseEnter={closeSubmenu}
              onFocus={closeSubmenu}
              role="menuitem"
            >
              Go to parent component
            </button>
            <button
              type="button"
              className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
              onClick={onDetachInstance}
              onMouseEnter={closeSubmenu}
              onFocus={closeSubmenu}
              role="menuitem"
            >
              Detach instance
            </button>
          </>
        ) : null}
        {isMulti && onGroup ? (
          <button
            type="button"
            className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]"
            onClick={onGroup}
            onMouseEnter={closeSubmenu}
            onFocus={closeSubmenu}
            role="menuitem"
          >
            <span>Group selection</span>
            <span className="pl-5 text-[11px] text-slate-500">Cmd/Ctrl+G</span>
          </button>
        ) : null}
        {isMulti && onWrapInFrame ? (
          <button
            type="button"
            className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]"
            onClick={onWrapInFrame}
            onMouseEnter={closeSubmenu}
            onFocus={closeSubmenu}
            role="menuitem"
          >
            <span>Wrap in Frame</span>
            <span className="pl-5 text-[11px] text-slate-500">Cmd/Ctrl+Alt+G</span>
          </button>
        ) : null}
        {!isMulti ? (
          <button
            type="button"
            className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
            onClick={onToggleMask}
            onMouseEnter={closeSubmenu}
            onFocus={closeSubmenu}
            role="menuitem"
          >
            {isMask ? "Remove mask" : "Use as a mask"}
          </button>
        ) : null}
        <SubmenuButton
          label="Style"
          active={activeSubmenu === "style"}
          onOpen={element => openSubmenu("style", element)}
        />
        <div className="my-1 h-px bg-white/10" />
        <button
          type="button"
          className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]"
          onClick={onCreateComponent}
          onMouseEnter={closeSubmenu}
          onFocus={closeSubmenu}
          role="menuitem"
        >
          <span className="whitespace-nowrap">Create a component</span>
          <span className="pl-5 text-[11px] text-slate-500">Alt+Cmd/Ctrl+K</span>
        </button>
        {!isMulti && convertTarget ? (
          <button
            type="button"
            className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
            onClick={onConvertFrameGroup}
            onMouseEnter={closeSubmenu}
            onFocus={closeSubmenu}
            role="menuitem"
          >
            Convert to {convertTarget === "frame" ? "Frame" : "Group"}
          </button>
        ) : null}
        {!isMulti && allowScripts && isFrame ? (
          <>
            <button
              type="button"
              className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
              onClick={onAttachOrChangeScript}
              onMouseEnter={closeSubmenu}
              onFocus={closeSubmenu}
              role="menuitem"
            >
              {hasScript ? "Change script…" : "Attach script…"}
            </button>
            {hasScript ? (
              <>
                <button
                  type="button"
                  className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
                  onClick={onEditScript}
                  onMouseEnter={closeSubmenu}
                  onFocus={closeSubmenu}
                  role="menuitem"
                >
                  Edit script
                </button>
                <button
                  type="button"
                  className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
                  onClick={onDetachScript}
                  onMouseEnter={closeSubmenu}
                  onFocus={closeSubmenu}
                  role="menuitem"
                >
                  Detach script
                </button>
                <button
                  type="button"
                  className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
                  onClick={onRunScript}
                  onMouseEnter={closeSubmenu}
                  onFocus={closeSubmenu}
                  role="menuitem"
                >
                  Run script
                </button>
              </>
            ) : null}
          </>
        ) : null}
        <div className="my-1 h-px bg-white/10" />
        <button
          type="button"
          className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]"
          onClick={onFlatten}
          onMouseEnter={closeSubmenu}
          onFocus={closeSubmenu}
          role="menuitem"
        >
          <span>Flatten</span>
          <span className="pl-5 text-[11px] text-slate-500">Cmd/Ctrl+E</span>
        </button>
        <button
          type="button"
          className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]"
          onClick={onPlanarize}
          onMouseEnter={closeSubmenu}
          onFocus={closeSubmenu}
          role="menuitem"
        >
          <span>Planarize</span>
          <span className="pl-5 text-[11px] text-slate-500">Cmd/Ctrl+Shift+E</span>
        </button>
        <button
          type="button"
          className="flex h-8 w-full items-center justify-between rounded px-2 text-left hover:bg-white/[0.08]"
          onClick={onOutlineStroke}
          onMouseEnter={closeSubmenu}
          onFocus={closeSubmenu}
          role="menuitem"
        >
          <span>Outline Stroke</span>
          <span className="pl-5 text-[11px] text-slate-500">Cmd/Ctrl+Alt+O</span>
        </button>
        <SubmenuButton
          label="Copy as"
          active={activeSubmenu === "copyAs"}
          onOpen={element => openSubmenu("copyAs", element)}
        />
        <div className="my-1 h-px bg-white/10" />
        <SubmenuButton
          label="Export as"
          active={activeSubmenu === "exportAs"}
          onOpen={element => openSubmenu("exportAs", element)}
        />
      </div>
      {submenu}
    </>
  );
}

function clampedSubmenuPosition(anchor: DOMRect, width: number, height: number): { left: number; top: number } {
  const margin = 8;
  const gap = 4;
  const right = anchor.right + gap;
  const left = anchor.left - width - gap;
  const preferredLeft = right + width <= window.innerWidth - margin ? right : left;
  return {
    left: Math.min(Math.max(margin, preferredLeft), Math.max(margin, window.innerWidth - width - margin)),
    top: Math.min(Math.max(margin, anchor.top), Math.max(margin, window.innerHeight - height - margin)),
  };
}

function SubmenuButton({
  label,
  active,
  onOpen,
}: {
  label: string;
  active: boolean;
  onOpen: (element: HTMLElement) => void;
}) {
  return (
    <button
      type="button"
      className={`flex h-8 w-full items-center justify-between rounded px-2 text-left ${active ? "bg-white/[0.08]" : "hover:bg-white/[0.08]"}`}
      onMouseEnter={event => onOpen(event.currentTarget)}
      onFocus={event => onOpen(event.currentTarget)}
      onClick={event => onOpen(event.currentTarget)}
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={active}
    >
      <span>{label}</span>
      <span className="pl-5 text-slate-500">›</span>
    </button>
  );
}
